import React, {
  createContext, useContext, useReducer, ReactNode,
} from 'react';

const speedMultiplier = {
  'M_S' : 1,
  'M_MIN' : 60,
  'KM_H' : 3.6,
}

export interface GlobalAppState {
  teamIsChanging: string | false;
  speedMultiplier: number;
  speedUOM: keyof typeof speedMultiplier;
}

export type GlobalAppAction =
  | { type: 'SET_TEAM_CHANGING'; payload: string | false }
  | { type: 'SET_SPEED_MULTIPLIER'; payload: {uom: keyof typeof speedMultiplier, value: number} };

// Stato iniziale
export const initialAppState: GlobalAppState = {
  teamIsChanging: false,
  speedMultiplier: speedMultiplier['M_S'],
  speedUOM: 'M_S',
};

// Reducer
export const globalReducer = (state: GlobalAppState, action: GlobalAppAction): GlobalAppState => {
  switch (action.type) {
    case 'SET_TEAM_CHANGING':
      return { ...state, teamIsChanging: action.payload };

    case 'SET_SPEED_MULTIPLIER':
      return { ...state, speedMultiplier: action.payload.value, speedUOM: action.payload.uom };

    default:
      return state;
  }
};

// Context
interface GlobalAppContextType {
  state: GlobalAppState;
  dispatch: React.Dispatch<GlobalAppAction>;
}

const GlobalAppContext = createContext<GlobalAppContextType | undefined>(undefined);

// Provider
export const GlobalAppStateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(globalReducer, initialAppState);

  const memoValue = React.useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return (
    <GlobalAppContext.Provider value={memoValue}>
      {children}
    </GlobalAppContext.Provider>
  );
};

// Hook per utilizzare lo stato globale
export const useGlobalAppState = () => {
  const context = useContext(GlobalAppContext);

  if (!context) {
    throw new Error('useGlobalAppState deve essere utilizzato all\'interno di GlobalStateProvider');
  }

  const { state, dispatch } = context;

  const setTeamIsChanging = (value: string | false) => {
    dispatch({ type: 'SET_TEAM_CHANGING', payload: value });
  };

  const setSpeedUOM = (value: keyof typeof speedMultiplier) => {
    dispatch({ type: 'SET_SPEED_MULTIPLIER', payload: { uom: value, value: speedMultiplier[value] || 1} });
  };

  return {
    teamIsChanging: state.teamIsChanging,
    speedMultiplier: state.speedMultiplier,
    setTeamIsChanging,
    setSpeedUOM,
  };
};
